/* eslint-disable no-console */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { HeadingLevel } from 'src/typings/headingTags'
import { useSession } from 'src/sdk/session'
import { useProductsQuery } from 'src/sdk/product/useProductsQuery'
import { useInView } from 'react-intersection-observer'
import Slider from 'react-slick'
import DynamicHeadingTags from 'src/components/common/DynamicHeadingTags/DynamicHeadingTags'
import useWindowDimensions from 'src/hooks/useWindowDimensions'
import InsiderDefaulProductCard from './InsiderDefaulProductCard'
import '../styles.scss'
import CustomArrow from '../SlickArrows/CustomArrow'
import InsiderDots from '../InsiderDots/InsiderDots'

type Props = {
  insiderShelfTitle: string
  headingTitle: HeadingLevel
  fontfamily: string
  collectionId: string
}

function InsiderShelfDefault({
  insiderShelfTitle,
  collectionId,
  headingTitle,
  fontfamily,
}: Props) {
  const sliderRef = useRef<Slider>(null)

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.75,
  })

  const { isMobile } = useWindowDimensions()
  const { channel } = useSession()

  if (!channel) {
    throw new Error(`useProduct: 'channel' from session is an empty string.`)
  }

  const productList = useProductsQuery({
    first: 12,
    after: '',
    sort: 'score_desc',
    term: '',
    selectedFacets: [
      { key: 'productClusterIds', value: collectionId },
      { key: 'channel', value: channel },
    ],
  })

  const products = useMemo(
    () => productList?.edges?.map((edge) => edge.node) ?? [],
    [productList]
  )

  const slidesToShow = useMemo(() => {
    if (isMobile) return Math.min(2, products.length)
    return Math.min(4, products.length)
  }, [isMobile, products.length])

  const [currentPage, setCurrentPage] = useState(0)
  const maxVisibleIndicators = 6


 const settings = useMemo(
    () => ({
      arrows: true,
      prevArrow: <CustomArrow direction="prev" />,
      nextArrow: <CustomArrow direction="next" />,
      infinite: false,
      slidesToShow,
      slidesToScroll: slidesToShow,
      afterChange: (firstIndex: number) => {
        setCurrentPage(Math.floor(firstIndex / slidesToShow))
      },
      dots: true,

      appendDots: (_dots: React.ReactNode[]) => (
        <InsiderDots
          length={products.length}
          currentSlider={currentPage}
          maxVisibleIndicators={maxVisibleIndicators}
          refSlider={sliderRef}
          isMobile={isMobile}
          itemsPerPage={slidesToShow}
          backgroundDotsActive="#000"
          backgroundDotsInactive="#ccc"
        />
      ),
      centerMode: false,
      className: 'insider-default-shelf',
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: Math.min(2, products.length),
            slidesToScroll: Math.min(2, products.length),
          },
        },
      ],
    }),
    [slidesToShow, products.length, currentPage, isMobile]
  )
  useEffect(() => {
    if (!products.length) return
  }, [inView, products, insiderShelfTitle])

  if(!products?.length){
    return null
  }

  return (
    <section className="insider-shelf  shelf-default">
      <div className="shelf-header">
        <DynamicHeadingTags
          className="insider-shelf__title"
          as={headingTitle}
          style={{
            fontFamily: fontfamily === '2' ? 'roboto' : 'Cormorant Garamond',
          }}
        >
          {insiderShelfTitle}
        </DynamicHeadingTags>
      </div>

      <div className={`insider-shelf__slider `} ref={ref}>
        {products.length > 0 && (
          <Slider ref={sliderRef} {...settings}>
            {products.map((product, idx) => (
              <InsiderDefaulProductCard
                key={product.id}
                product={product}
                index={idx}
              />
            ))}
          </Slider>
        )}
      </div>
    </section>
  )
}

export default InsiderShelfDefault
